import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import {
  SearchResponse,
  ClubInfo,
  Competition,
  SearchResult,
} from './types';

// Cache interface
interface CacheItem<T> {
  data: T;
  timestamp: number;
}

// Cache store
const cache = new Map<string, CacheItem<any>>();

// Create axios instance with base URL from environment variables
const apiClient: AxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_FACR_API_BASE_URL || '/api/v1/facr',
  timeout: parseInt(process.env.REACT_APP_FACR_API_TIMEOUT || '20000', 10),
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Helper: resolve backend absolute URL for /api and /cache paths
const resolveBackendUrl = (path: string) => {
  try {
    if (/^https?:\/\//i.test(path)) return path;
    if (path.startsWith('/cache') || path.startsWith('/uploads') || path.startsWith('/api/')) {
      const explicit = process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_API_URL || '';
      if (typeof window === 'undefined') {
        // In non-browser contexts, avoid forcing any origin; return the relative path
        return path;
      }
      const baseOrigin = explicit
        ? new URL(explicit, window.location.origin).origin
        : window.location.origin;
      // Use URL constructor so query strings in `path` are handled correctly
      return new URL(path, baseOrigin).toString();
    }
    return path;
  } catch {
    return path;
  }
};

// Lazy-load public overrides with lightweight cache
let overridesCache: { data: any; ts: number } | null = null;
type OverridesPayload = {
  by_name?: Record<string, string>;
  by_id?: Record<string, { name?: string; logo_url?: string }>;
};
const loadOverrides = async (): Promise<OverridesPayload> => {
  const now = Date.now();
  if (overridesCache && now - overridesCache.ts < 60_000) {
    return (overridesCache.data || {}) as OverridesPayload;
  }
  try {
    const res = await fetch(resolveBackendUrl(`/api/v1/public/team-logo-overrides?t=${now}`), { cache: 'no-cache' });
    if (res.ok) {
      const json = await res.json();
      const prev = JSON.stringify(overridesCache?.data || {});
      const next = JSON.stringify(json || {});
      overridesCache = { data: json, ts: now };
      if (prev !== next) {
        // Invalidate internal FACR GET cache so consumers refetch with new logos
        cache.clear();
      }
      return (json || {}) as OverridesPayload;
    }
  } catch {}
  // Fallback to cached file if API failed
  try {
    const res2 = await fetch(resolveBackendUrl('/cache/prefetch/team_logo_overrides.json'), { cache: 'no-cache' });
    if (res2.ok) {
      const json = await res2.json();
      const prev = JSON.stringify(overridesCache?.data || {});
      const next = JSON.stringify(json || {});
      overridesCache = { data: json, ts: now };
      if (prev !== next) {
        cache.clear();
      }
      return (json || {}) as OverridesPayload;
    }
  } catch {}
  overridesCache = { data: { by_name: {} }, ts: now };
  return { by_name: {} };
};

// Name normalization helpers
const norm = (s: string) => String(s || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/\s+/g, ' ')
  .trim()
  .toLowerCase();
const stripPrefixes = (s: string) => {
  let x = norm(s);
  // Remove common Czech tokens/prefixes
  x = x.replace(/\b(mestsky|m\.?f\.?k\.?|mfk|tj|sk|sokol|fotbalovy|fotbalový|fotbalovy\s+klub|fotbalovy\s+klub)\b/g, '');
  return x.replace(/\s+/g, ' ').trim();
};
const applyOverridesToClub = (club: ClubInfo, overrides: OverridesPayload) => {
  if (!club?.competitions?.length) return club;
  const byName = overrides?.by_name || {};
  const byId = overrides?.by_id || {} as Record<string, { name?: string; logo_url?: string }>;
  const byNameNorm: Record<string, string> = Object.keys(byName || {}).reduce((acc: Record<string, string>, k) => {
    acc[norm(k)] = byName[k];
    return acc;
  }, {});
  const strippedPairs = Object.keys(byName || {}).map((k) => ({ key: stripPrefixes(k), url: byName[k] }));
  const pickLogo = (teamId?: string, teamName?: string, original?: string) => {
    if (teamId && byId[teamId]?.logo_url) {
      const v = byId[teamId]!.logo_url!;
      if (typeof v === 'string' && v.startsWith('/')) return resolveBackendUrl(v);
      return v;
    }
    if (!teamName) return original;
    const exact = (byName || {})[teamName];
    const n = norm(teamName);
    let candidate = exact || byNameNorm[n];
    if (!candidate) {
      const s = stripPrefixes(teamName);
      for (const { key, url } of strippedPairs) {
        if (!key) continue;
        if (s.endsWith(key) || key.endsWith(s)) { candidate = url; break; }
      }
    }
    const chosen = candidate || original;
    if (typeof chosen === 'string' && chosen.startsWith('/')) return resolveBackendUrl(chosen);
    return chosen;
  };
  const pickName = (teamId?: string, original?: string) => {
    const v = (teamId && byId[teamId]?.name) ? byId[teamId]!.name : undefined;
    return (v && v.trim().length > 0) ? v : original;
  };
  club.competitions = (club.competitions || []).map((c) => ({
    ...c,
    matches: (c.matches || []).map((m: any) => ({
      ...m,
      home: pickName(m.home_id, m.home),
      away: pickName(m.away_id, m.away),
      home_logo_url: pickLogo(m.home_id, m.home, m.home_logo_url),
      away_logo_url: pickLogo(m.away_id, m.away, m.away_logo_url),
    })),
  }));
  return club;
};

// Request interceptor for caching
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Skip cache for non-GET requests
    if (config.method?.toLowerCase() !== 'get') {
      return config;
    }

    // Generate a cache key based on the request
    const cacheKey = `${config.method}:${config.url}:${JSON.stringify(config.params)}`;
    
    // Check if we have a cached response
    const cached = cache.get(cacheKey);
    const now = Date.now();
    const cacheTtl = parseInt(process.env.REACT_APP_FACR_CACHE_TTL || '3600000', 10);
    
    if (cached && now - cached.timestamp < cacheTtl) {
      // Return cached response
      return {
        ...config,
        adapter: () => Promise.resolve({
          data: cached.data,
          status: 200,
          statusText: 'OK',
          headers: {},
          config,
        }),
      };
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for caching successful responses
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    const method = response.config.method?.toLowerCase();
    const url = response.config.url;
    const params = response.config.params;
    
    // Only cache GET requests
    if (method === 'get') {
      const cacheKey = `${method}:${url}:${JSON.stringify(params)}`;
      cache.set(cacheKey, {
        data: response.data,
        timestamp: Date.now(),
      });
    }
    return response;
  },
  (error) => {
    console.error('API Error:', error);
    return Promise.reject(error);
  }
);

// Helper function to handle API errors
const handleApiError = (error: any) => {
  if (error.response) {
    // The request was made and the server responded with a status code
    // that falls out of the range of 2xx
    const errorData = error.response.data || {};
    const errorMessage = errorData.message || 'API request failed';
    console.error('API Error Response:', errorData);
    throw new Error(errorMessage);
  } else if (error.request) {
    // The request was made but no response was received
    console.error('API Error: No response received', error.request);
    throw new Error('No response from server. Please check if the FACR scraper API is running.');
  } else {
    // Something happened in setting up the request that triggered an Error
    console.error('API Error:', error.message);
    throw new Error(`API request failed: ${error.message}`);
  }
};

// FACR API client
export const facrApi = {
  // Search for clubs
  searchClubs: async (query: string): Promise<SearchResponse> => {
    try {
      const response = await apiClient.get<SearchResponse>('/club/search', {
        params: { q: query },
      });
      return response.data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Get club details and matches
  getClub: async (clubId: string, clubType: 'football' | 'futsal' = 'football'): Promise<ClubInfo> => {
    try {
      const response = await apiClient.get<ClubInfo>(`/club/${clubType}/${clubId}`);
      // Load overrides and apply before returning/caching consumers
      const overrides = await loadOverrides();
      const patched = applyOverridesToClub(response.data, overrides);
      return patched;
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Get club table (standings)
  getClubTable: async (clubId: string, clubType: 'football' | 'futsal' = 'football'): Promise<ClubInfo> => {
    try {
      const response = await apiClient.get<ClubInfo>(`/club/${clubType}/${clubId}/table`);
      const data = response.data as any;
      const overrides = await loadOverrides();
      const byName = overrides?.by_name || {};
      const byId = overrides?.by_id || {} as Record<string, { name?: string; logo_url?: string }>;
      const byNameNorm: Record<string, string> = Object.keys(byName || {}).reduce((acc: Record<string, string>, k) => { acc[norm(k)] = byName[k]; return acc; }, {});
      const strippedPairs = Object.keys(byName || {}).map((k) => ({ key: stripPrefixes(k), url: byName[k] }));
      const pickLogo = (teamId?: string, teamName?: string, original?: string) => {
        if (teamId && byId[teamId]?.logo_url) {
          const v = byId[teamId]!.logo_url!;
          if (typeof v === 'string' && v.startsWith('/')) return resolveBackendUrl(v);
          return v;
        }
        if (!teamName) return original;
        const exact = (byName || {})[teamName];
        const n = norm(teamName);
        let candidate = exact || byNameNorm[n];
        if (!candidate) {
          const s = stripPrefixes(teamName);
          for (const { key, url } of strippedPairs) { if (!key) continue; if (s.endsWith(key) || key.endsWith(s)) { candidate = url; break; } }
        }
        const chosen = candidate || original;
        if (typeof chosen === 'string' && chosen.startsWith('/')) return resolveBackendUrl(chosen);
        return chosen;
      };
      const pickName = (teamId?: string, original?: string) => {
        const v = (teamId && byId[teamId]?.name) ? byId[teamId]!.name : undefined;
        return (v && v.trim().length > 0) ? v : original;
      };
      if (Array.isArray(data?.competitions)) {
        data.competitions = data.competitions.map((c: any) => ({
          ...c,
          table: {
            overall: (c.table?.overall || []).map((r: any) => ({
              ...r,
              team: pickName(r.team_id, r.team),
              team_logo_url: pickLogo(r.team_id, r.team, r.team_logo_url),
            })),
          },
        }));
      }
      return data;
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Clear cache
  clearCache: (): void => {
    cache.clear();
  },

  // Get all competitions for a club
  getClubCompetitions: async (clubId: string, clubType: 'football' | 'futsal' = 'football'): Promise<Competition[]> => {
    try {
      const clubInfo = await facrApi.getClub(clubId, clubType);
      return clubInfo.competitions || [];
    } catch (error) {
      return handleApiError(error);
    }
  },

  // Get matches for a specific competition
  getCompetitionMatches: async (competitionId: string): Promise<any[]> => {
    try {
      // Note: This assumes the competition ID is in the format 'type/id'
      const [type, id] = competitionId.split('/');
      const response = await apiClient.get<ClubInfo>(`/club/${type}/${id}`);
      // Find the specific competition and return its matches
      const competition = response.data.competitions?.find(c => c.id === competitionId);
      return competition?.matches || [];
    } catch (error) {
      return handleApiError(error);
    }
  },
};

export default facrApi;
